import './editor/pendingLink.css';

import {Box} from '@dropbox/dig-foundations';
import {CodeHighlightNode, CodeNode} from '@lexical/code';
import {$generateNodesFromDOM} from '@lexical/html';
import {$createLinkNode, $isLinkNode, AutoLinkNode, LinkNode} from '@lexical/link';
import {ListItemNode, ListNode} from '@lexical/list';
import {
  $convertFromMarkdownString,
  ELEMENT_TRANSFORMERS,
  type ElementTransformer,
  TEXT_FORMAT_TRANSFORMERS,
  TEXT_MATCH_TRANSFORMERS,
  type Transformer,
} from '@lexical/markdown';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {
  $createHorizontalRuleNode,
  $isHorizontalRuleNode,
  HorizontalRuleNode,
} from '@lexical/react/LexicalHorizontalRuleNode';
import {ListPlugin} from '@lexical/react/LexicalListPlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {TablePlugin} from '@lexical/react/LexicalTablePlugin';
import {HeadingNode, QuoteNode} from '@lexical/rich-text';
import {$isTableNode, TableCellNode, TableNode, TableRowNode} from '@lexical/table';
import {normalizeListIndent} from 'helpers/markdownListIndent';
import {extractTable} from 'helpers/markdownTable';
import {
  $createParagraphNode,
  $createTextNode,
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
  $isLineBreakNode,
  $isParagraphNode,
  type ElementNode,
  type Klass,
  type LexicalEditor,
  type LexicalNode,
} from 'lexical';
import {useEffect, useMemo} from 'react';

import styles from './editor/Editor.module.css';
import {AutoLinkPlugin} from './editor/plugins/AutoLinkPlugin';
import {LexicalClickableLinkPlugin} from './editor/plugins/LexicalClickableLinkPlugin';
import getEditorTheme, {
  type EditorTextColor,
  type EditorTextSize,
} from './editor/themes/EditorTheme';
import tableTheme from './editor/themes/TableTheme.module.css';
import {$createPersonChipNode, PersonChipNode} from './MarkdownPersonChipNode';
import {$createStatusChipNode, isStatusValue, StatusChipNode} from './MarkdownStatusChipNode';

/**
 * Thematic break (`---`, `***`, `___`). `@lexical/markdown` ships no transformer
 * for it, so without this a rule line renders as the literal text "---".
 */
const HORIZONTAL_RULE: ElementTransformer = {
  // eslint-disable-next-line deprecation/deprecation -- matches the node registered in MARKDOWN_NODES
  dependencies: [HorizontalRuleNode],
  export: (node) => ($isHorizontalRuleNode(node) ? '---' : null),
  regExp: /^(---|\*\*\*|___)\s*$/,
  replace: (parentNode) => {
    // eslint-disable-next-line deprecation/deprecation -- must construct the registered node class
    parentNode.replace($createHorizontalRuleNode());
  },
  type: 'element',
};

/**
 * Stock markdown transformers (block elements, inline formatting, links) plus
 * {@link HORIZONTAL_RULE}. We do NOT include the app's table transformer here:
 * it targets a `TableNode` API that no longer exists and silently fails.
 * GitHub-style tables are handled separately in {@link $upgradeMarkdownTables}.
 */
const MARKDOWN_TRANSFORMERS: Transformer[] = [
  ...ELEMENT_TRANSFORMERS,
  HORIZONTAL_RULE,
  ...TEXT_FORMAT_TRANSFORMERS,
  ...TEXT_MATCH_TRANSFORMERS,
];

/**
 * Minimal node set: block elements, inline links, tables, and the lightweight
 * {@link PersonChipNode} / {@link StatusChipNode} decorators used for person and
 * status references in answer tables. Deliberately excludes the heavy editor
 * nodes (mentions, redactions, media) so this stays cheap to mount and re-render
 * on every streamed token.
 */
const MARKDOWN_NODES: Array<Klass<LexicalNode>> = [
  HeadingNode,
  QuoteNode,
  ListNode,
  ListItemNode,
  CodeNode,
  CodeHighlightNode,
  LinkNode,
  AutoLinkNode,
  // eslint-disable-next-line deprecation/deprecation -- matches editorNodes.ts; markdown thematic breaks still use this node
  HorizontalRuleNode,
  TableNode,
  TableRowNode,
  TableCellNode,
  PersonChipNode,
  StatusChipNode,
];

/** Default per-column width (px) for imported tables; `table-layout: fixed` needs explicit widths. */
const DEFAULT_COLUMN_WIDTH = 150;

/**
 * A header the model wrote as a raw field identifier: all-lowercase, snake_case
 * or dotted (e.g. `metric_status`, `workstream.name`). Headers it already wrote
 * for humans (any uppercase or whitespace, e.g. `Workstream`, `Last update`,
 * `DRIs`) don't match and are left untouched.
 */
const RAW_HEADER_PATTERN = /^[a-z][a-z0-9]*([_.][a-z0-9]+)*$/;

/** Humanize a raw-identifier header for display: `metric_status` -> `Metric Status`. */
function humanizeHeader(header: string): string {
  const trimmed = header.trim();
  if (!RAW_HEADER_PATTERN.test(trimmed)) {
    return header;
  }
  return trimmed.replace(/[_.]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Allow only safe in-app or web URLs in table-cell links: relative app paths
 * (a single leading slash, not protocol-relative `//`) or http(s). Everything
 * else (javascript:, data:, etc.) is rejected so cell content can never inject
 * an unsafe link.
 */
function isSafeCellUrl(url: string): boolean {
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }
  return /^https?:\/\//i.test(url);
}

const CELL_LINK_PATTERN = /\[((?:\\.|[^\]\\])*)\]\(([^)\s]+)\)/g;

/** Remove escapes needed only to keep brackets unambiguous inside a Markdown link label. */
function unescapeCellLinkLabel(label: string): string {
  return label.replace(/\\([\\[\]])/g, '$1');
}

/** Matches the `/people/{ldap}` profile route that renders as a person chip. */
const PERSON_LINK_PATTERN = /^\/people\/([^/?#]+)$/;

/** Matches the `/status/{value}` pseudo-route that renders as a status chip. */
const STATUS_LINK_PATTERN = /^\/status\/([a-z_]+)$/;

/**
 * Build the inline Lexical nodes for one table cell:
 * - a cell that is exactly a known status value (e.g. `at_risk`) becomes a
 *   {@link StatusChipNode}, so the model can emit the raw value with no markup;
 * - safe Markdown links `[text](url)` become a {@link PersonChipNode}
 *   (`/people/{ldap}`), a {@link StatusChipNode} (`/status/{value}`), or a
 *   {@link LinkNode} opening in a new tab for any other safe URL.
 * Unknown status values, unsafe links, and plain text stay as {@link TextNode}s.
 */
function $cellInlineNodes(cell: string): LexicalNode[] {
  if (isStatusValue(cell.trim())) {
    return [$createStatusChipNode(cell.trim())];
  }

  const nodes: LexicalNode[] = [];
  let lastIndex = 0;
  CELL_LINK_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = CELL_LINK_PATTERN.exec(cell)) !== null) {
    const [raw, text, url] = match;
    const label = unescapeCellLinkLabel(text);
    const before = cell.slice(lastIndex, match.index);
    if (before) {
      nodes.push($createTextNode(before));
    }
    if (isSafeCellUrl(url)) {
      const person = PERSON_LINK_PATTERN.exec(url);
      const status = STATUS_LINK_PATTERN.exec(url);
      if (person) {
        nodes.push($createPersonChipNode(person[1], label));
      } else if (status && isStatusValue(status[1])) {
        nodes.push($createStatusChipNode(status[1]));
      } else if (status) {
        nodes.push($createTextNode(label));
      } else {
        const link = $createLinkNode(url, {rel: 'noopener noreferrer', target: '_blank'});
        link.append($createTextNode(label));
        nodes.push(link);
      }
    } else {
      nodes.push($createTextNode(raw));
    }
    lastIndex = match.index + raw.length;
  }
  const rest = cell.slice(lastIndex);
  if (rest || nodes.length === 0) {
    nodes.push($createTextNode(rest));
  }
  return nodes;
}

/**
 * Replace cell contents with real inline nodes for cells that need richer
 * rendering: those containing Markdown links, or those that are exactly a known
 * status value. Lexical's table DOM import flattens cell anchors to plain text,
 * so we rebuild the affected cells from the original source after the skeleton
 * imports. ``sourceRows`` is ``[headerCells, ...bodyRows]`` aligned to the rows.
 */
function $applyCellLinks(tableNode: LexicalNode, sourceRows: string[][]): void {
  if (!$isElementNode(tableNode)) {
    return;
  }
  const rows = tableNode.getChildren();
  rows.forEach((row, r) => {
    if (!$isElementNode(row)) {
      return;
    }
    row.getChildren().forEach((cell, c) => {
      const source = sourceRows[r]?.[c];
      const needsUpgrade = !!source && (source.includes('](') || isStatusValue(source.trim()));
      if (!needsUpgrade || !$isElementNode(cell)) {
        return;
      }
      const inline = $cellInlineNodes(source);
      const paragraph = cell.getFirstChild();
      if ($isElementNode(paragraph)) {
        paragraph.clear();
        inline.forEach((node) => paragraph.append(node));
      } else {
        const p = $createParagraphNode();
        inline.forEach((node) => p.append(node));
        cell.clear();
        cell.append(p);
      }
    });
  });
}

/**
 * Build table nodes via Lexical's `importDOM` from an HTML `<table>`. Hand-built
 * `TableNode`s are rejected/normalized away by `TablePlugin`, whereas DOM import
 * produces the grid structure the plugin expects. Cell text is HTML-escaped for
 * the skeleton; safe Markdown links are then injected as {@link LinkNode}s via
 * {@link $applyCellLinks} (the importer drops anchors inside cells).
 */
function $tableNodesFromMarkdown(
  editor: LexicalEditor,
  headerCells: string[],
  bodyRows: string[][]
): LexicalNode[] {
  const columnCount = headerCells.length;
  const headerHtml = headerCells
    .map((cell) => `<th>${escapeHtml(humanizeHeader(cell))}</th>`)
    .join('');
  const bodyHtml = bodyRows
    .map((row) => {
      const cells: string[] = [];
      for (let c = 0; c < columnCount; c++) {
        cells.push(`<td>${escapeHtml(row[c] ?? '')}</td>`);
      }
      return `<tr>${cells.join('')}</tr>`;
    })
    .join('');
  const html = `<table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table>`;

  const dom = new DOMParser().parseFromString(html, 'text/html');
  const nodes = $generateNodesFromDOM(editor, dom).filter(
    (node) => $isElementNode(node) || $isDecoratorNode(node)
  );

  const sourceRows = [headerCells, ...bodyRows];

  // The editor theme uses `table-layout: fixed`, which sizes columns purely from
  // the table's `colgroup`. DOM-imported tables carry no widths, so the columns
  // (and therefore the whole table) collapse to ~0. Assign explicit widths so the
  // table renders; it scrolls horizontally when the columns exceed the container.
  for (const node of nodes) {
    if ($isTableNode(node)) {
      node.setColWidths(new Array(columnCount).fill(DEFAULT_COLUMN_WIDTH));
      $applyCellLinks(node, sourceRows);
    }
  }
  return nodes;
}

/**
 * Reconstruct the Markdown source of a paragraph, re-serializing {@link LinkNode}s
 * back to `[text](url)`. `$convertFromMarkdownString` linkifies inline content
 * before tables are upgraded, so reading {@link LexicalNode#getTextContent} alone
 * would drop link URLs from the table cells.
 */
function $paragraphMarkdown(node: ElementNode): string {
  let out = '';
  for (const child of node.getChildren()) {
    if ($isLinkNode(child)) {
      out += `[${child.getTextContent()}](${child.getURL()})`;
    } else if ($isLineBreakNode(child)) {
      out += '\n';
    } else {
      out += child.getTextContent();
    }
  }
  return out;
}

function $createTextParagraph(text: string): LexicalNode {
  const paragraph = $createParagraphNode();
  paragraph.append($createTextNode(text));
  return paragraph;
}

/**
 * After standard import, a GFM table lands in a single paragraph with line
 * breaks (the importer joins consecutive `|`-rows into one block). Split such
 * paragraphs and replace the table portion with a real {@link TableNode}.
 *
 * Each paragraph is handled independently and defensively: if table generation
 * fails we leave the original paragraph untouched rather than corrupting the
 * surrounding content.
 */
function $upgradeMarkdownTables(editor: LexicalEditor, root: ElementNode): void {
  for (const node of root.getChildren()) {
    if (!$isParagraphNode(node)) {
      continue;
    }
    const text = $paragraphMarkdown(node);
    // A full table needs at least a header + separator line.
    if (!text.includes('|') || !text.includes('\n')) {
      continue;
    }
    const lines = text.split('\n');
    const table = extractTable(lines);
    if (!table) {
      continue;
    }

    try {
      const tableNodes = $tableNodesFromMarkdown(editor, table.header, table.rows);
      if (tableNodes.length === 0) {
        continue;
      }
      const before = lines.slice(0, table.startIdx);
      const after = lines.slice(table.endIdx);
      if (before.some((l) => l.trim())) {
        node.insertBefore($createTextParagraph(before.join('\n')));
      }
      tableNodes.forEach((tableNode) => node.insertBefore(tableNode));
      if (after.some((l) => l.trim())) {
        node.insertBefore($createTextParagraph(after.join('\n')));
      }
      node.remove();
    } catch {
      // Leave the paragraph as-is on failure (renders as raw table text).
    }
  }
}

/**
 * Walk the tree and convert `[label](/status/{value})` links in prose to
 * {@link StatusChipNode}s — the model's explicit, unambiguous status signal, which
 * works for every status including single-word ones like `complete`. Tables are
 * skipped: their cells (including bare status values) are handled by
 * {@link $applyCellLinks}. Anything that isn't a known status link is left as-is,
 * so an ordinary word like "complete" in a sentence never becomes a chip.
 */
function $upgradeInlineStatusLinks(node: ElementNode): void {
  for (const child of node.getChildren()) {
    if ($isTableNode(child)) {
      continue;
    }
    if ($isLinkNode(child)) {
      const status = STATUS_LINK_PATTERN.exec(child.getURL());
      if (status && isStatusValue(status[1])) {
        child.replace($createStatusChipNode(status[1]));
      }
      continue;
    }
    if ($isElementNode(child)) {
      $upgradeInlineStatusLinks(child);
    }
  }
}

/**
 * Detect a trailing incomplete markdown link at the end of streaming content and
 * replace the partial URL with a `#pending` placeholder so users never see a long
 * raw URL flash. The link text renders styled as a link with a shimmer underline
 * while the full URL streams in.
 *
 * Status chip links (`/status/{value}`) are completed eagerly so the chip renders
 * immediately instead of flashing as a blue link first.
 *
 * Handles patterns like:
 *   `[UnitQ](https://app.unitq.com/...`  (no closing paren)
 *   `[UnitQ](`                            (just opened the URL)
 *   `[UnitQ](https://a`                   (partial URL)
 *   `[UnitQ`                              (link text not closed yet)
 */
const TRAILING_INCOMPLETE_LINK = /\[([^\]]+)\]\(([^)]*)?$/;
const TRAILING_INCOMPLETE_LINK_TEXT = /\[([^\]]+)$/;

function textToStatusSlug(text: string): string {
  return text.trim().toLowerCase().replace(/ /g, '_');
}

function cleanStreamingLinks(markdown: string): string {
  const linkMatch = TRAILING_INCOMPLETE_LINK.exec(markdown);
  if (linkMatch) {
    const [, text, partialUrl = ''] = linkMatch;
    const slug = textToStatusSlug(text);
    if (partialUrl.startsWith('/status/') && isStatusValue(slug)) {
      return markdown.slice(0, linkMatch.index) + `[${text}](/status/${slug})`;
    }
    return markdown.slice(0, linkMatch.index) + `[${text}](#pending)`;
  }
  const textMatch = TRAILING_INCOMPLETE_LINK_TEXT.exec(markdown);
  if (textMatch) {
    return markdown.slice(0, textMatch.index) + textMatch[1];
  }
  return markdown;
}

/** Re-parses the markdown string into Lexical nodes whenever `value` changes. */
const MarkdownContentPlugin = ({value, isStreaming}: {value: string; isStreaming?: boolean}) => {
  const [editor] = useLexicalComposerContext();

  const cleaned = useMemo(
    () => normalizeListIndent(isStreaming ? cleanStreamingLinks(value) : value),
    [value, isStreaming]
  );

  useEffect(() => {
    editor.update(() => {
      const root = $getRoot();
      try {
        $convertFromMarkdownString(cleaned, MARKDOWN_TRANSFORMERS);
        $upgradeMarkdownTables(editor, root);
        $upgradeInlineStatusLinks(root);
      } catch {
        // Partial/invalid markdown (common mid-stream): show it as plain text
        // until enough tokens arrive for it to parse cleanly.
        root.clear();
        const paragraph = $createParagraphNode();
        paragraph.append($createTextNode(value));
        root.append(paragraph);
      }
    });
  }, [editor, value, cleaned]);

  return null;
};

export interface MarkdownViewProps {
  /** Markdown source. Safe to update frequently (e.g. token-by-token streaming). */
  value: string;
  textSize?: EditorTextSize;
  textColor?: EditorTextColor;
  /** Extra class applied to the content element (composed with the editor styles). */
  contentClassName?: string;
  /** When true, incomplete trailing links are buffered so the raw URL never flashes. */
  isStreaming?: boolean;
}

/**
 * Read-only Markdown renderer built on a bare Lexical composer.
 *
 * Reuses the app's editor theme so output matches `RichTextArea`, but without
 * collaboration, redaction, media, or toolbar plumbing. That makes it light
 * enough to re-render continuously while an answer streams in.
 */
export const MarkdownView = ({
  value,
  textSize = 'medium',
  textColor = 'standard',
  contentClassName,
  isStreaming,
}: MarkdownViewProps) => {
  return (
    <LexicalComposer
      initialConfig={{
        namespace: 'MarkdownView',
        editable: false,
        nodes: MARKDOWN_NODES,
        // The default editor theme hides tables (`.table { display: none }`):
        // only the "paper" profile renders them. We want tables here but not the
        // paper typography, so keep the default theme and swap in the real,
        // styled table class.
        theme: {...getEditorTheme({textSize, textColor}), table: tableTheme.table},
        editorState: null,
        onError(error: unknown) {
          throw error;
        },
      }}
    >
      <Box position="relative">
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={`${styles.content}${contentClassName ? ` ${contentClassName}` : ''}`}
            />
          }
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <ListPlugin />
        <TablePlugin hasHorizontalScroll />
        <LexicalClickableLinkPlugin newTab />
        <AutoLinkPlugin />
        <MarkdownContentPlugin value={value} isStreaming={isStreaming} />
      </Box>
    </LexicalComposer>
  );
};
